import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './InsightDetail.css';

const TYPE_LABELS = {
  en:      { blog: 'Blog', news: 'News', report: 'Report', podcast: 'Podcast' },
  'zh-CN': { blog: '博客', news: '新闻', report: '报告', podcast: '播客' },
  'zh-TW': { blog: '部落格', news: '新聞', report: '報告', podcast: '播客' },
  ja:      { blog: 'ブログ', news: 'ニュース', report: 'レポート', podcast: 'ポッドキャスト' },
};

const formatDate = (dateStr, lang) => {
  if (!dateStr) return '';
  const locale =
    lang === 'ja' ? 'ja-JP' : lang.startsWith('zh') ? 'zh-CN' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const InsightDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';

  const [article, setArticle] = useState(undefined); // undefined = loading, null = not found
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/news/${slug}?lang=${lang}`);
        setArticle(res.data.data || null);
      } catch (error) {
        console.error('Error fetching article:', error);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, lang]);

  const typeLabels = TYPE_LABELS[lang] || TYPE_LABELS.en;

  if (loading) {
    return (
      <div className="insight-detail-page">
        <div className="container" style={{ padding: '6rem 0', textAlign: 'center' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="insight-detail-page">
        <div className="insight-not-found container">
          <p>
            {lang === 'zh-CN'
              ? '找不到该文章。'
              : lang === 'zh-TW'
              ? '找不到該文章。'
              : lang === 'ja'
              ? '記事が見つかりませんでした。'
              : 'Article not found.'}
          </p>
          <Link to="/insights" className="insight-detail-back" style={{ marginTop: '1.5rem' }}>
            <FiArrowLeft />
            {lang === 'zh-CN'
              ? '返回洞察'
              : lang === 'zh-TW'
              ? '返回洞察'
              : lang === 'ja'
              ? 'インサイト一覧へ戻る'
              : 'Back to Insights'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="insight-detail-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <p className="page-label">{t('nav.ourInsights')}</p>
          {article.type && (
            <span className="type-tag">{typeLabels[article.type] || article.type}</span>
          )}
          <h1>{article.title}</h1>
          <div className="insight-detail-hero-meta">
            {article.author && (
              <span className="author-meta">{article.author}</span>
            )}
            {article.publishedAt && (
              <span className="author-meta">{formatDate(article.publishedAt, lang)}</span>
            )}
          </div>
        </div>
      </section>

      {/* Article body */}
      <section className="section">
        <div className="container">
          <div className="insight-detail-body">
            <div
              className="insight-detail-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="insight-detail-tags">
                {article.tags.map((tag) => (
                  <span key={tag} className="insight-detail-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Back link */}
            <Link to="/insights" className="insight-detail-back">
              <FiArrowLeft />
              {lang === 'zh-CN'
                ? '返回洞察'
                : lang === 'zh-TW'
                ? '返回洞察'
                : lang === 'ja'
                ? 'インサイト一覧へ戻る'
                : 'Back to Insights'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightDetail;
